import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import TenantSignupLayout from '../TenantSignupLayout';
import { getAppointments } from '../../components/TenantApp/getAppointments';
import { Auth, API, graphqlOperation } from 'aws-amplify';
import { createTenant } from '../../graphql/mutations';
import { driversByFacility } from '../../graphql/queries';
import { Driver } from '../../context/model/types';
import { getAvailableSlots, assignDriver, SlotKey, SLOT_TIMES } from '../../utils/driverAvailability';
import { markMatchingLeadsConverted } from '../../utils/leadAutoConvert';

type BaseSignupState = {
  facilityID: string;
  name: string;
  email: string;
  password: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  specialInstructions: string;
  size: string;
  parking: string;
  building: string;
  minStay: string;
  typeOfPlan: string;
  price: string;
  referralCode?: string;
};

type LocationState = BaseSignupState;

type Appointment = {
  facilityID: string;
  date: string;
  time: string;
  driver: string;
  [key: string]: any;
};

type SlotInfo = { slotKey: SlotKey; time: string; paid: boolean };

function getPaidPricing(planSize: string): string {
  return planSize?.includes('10') ? '$120 flat + $2/min' : '$60 flat + $1/min';
}

// ---- styles ----
const baseFont: CSSProperties = {
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  color: '#222',
  fontSize: 14,
};

const labelStyle: CSSProperties = {
  ...baseFont, fontSize: 13, fontWeight: 500, marginBottom: 4,
};

const inputStyle: CSSProperties = {
  width: '100%', padding: '10px 12px', borderRadius: 6,
  border: '1px solid #CCCCCC', fontSize: 14,
  fontFamily: baseFont.fontFamily, boxSizing: 'border-box' as const,
};

const hintTextStyle: CSSProperties = {
  ...baseFont, fontSize: 12, color: '#777', marginTop: 8,
};

const errorTextStyle: CSSProperties = {
  ...baseFont, fontSize: 12, color: '#d32f2f', marginTop: 4,
};

const chipStyle: CSSProperties = {
  ...baseFont,
  padding: '8px 12px', borderRadius: 16, border: '1px solid #7B1FA2',
  backgroundColor: 'transparent', color: '#7B1FA2', cursor: 'pointer', fontSize: 13,
};

const chipSelectedStyle: CSSProperties = { backgroundColor: '#7B1FA2', color: '#fff' };

// ---- helpers ----
function addDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function buildTenantInput(signupState: BaseSignupState) {
  return {
    id: signupState.email,
    facilityID: signupState.facilityID,
    name: signupState.name,
    email: signupState.email,
    phone: signupState.phone,
    address: {
      addressLine1: signupState.addressLine1,
      addressLine2: signupState.addressLine2,
      city: signupState.city,
      state: signupState.state,
      zip: signupState.zip,
      specialInstructions: signupState.specialInstructions,
      parking: signupState.parking,
      building: signupState.building,
    },
    plan: {
      minStay: signupState.minStay,
      typeOfPlan: signupState.typeOfPlan,
      size: signupState.size,
      price: signupState.price,
    },
    referralCode: signupState.referralCode?.trim() || undefined,
  };
}

const InitialAppointmentPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState | undefined>();
  const signupState = location.state;

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loadingAppointments, setLoadingAppointments] = useState<boolean>(true);

  const [selectedDate, setSelectedDate] = useState<string>('');
  const paidPricingLabel = getPaidPricing(signupState?.size ?? '');
  const [availableSlots, setAvailableSlots] = useState<SlotInfo[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const minDate = useMemo(() => addDays(2), []);

  useEffect(() => {
    console.log('[InitialAppointmentPage] route state:', signupState);
  }, [signupState]);

  if (!signupState) {
    return (
      <TenantSignupLayout
        title="Schedule your first pickup"
        subtitle="We need your signup details before we can schedule an appointment."
        step={5}
        totalSteps={7}
      >
        <div style={{ ...baseFont }}>
          <p style={{ marginBottom: 16 }}>
            It looks like you opened this page directly. Please go back to the beginning of signup.
          </p>
          <button
            type="button"
            onClick={() => history.push('/signup')}
            style={{
              ...baseFont, padding: '10px 16px', borderRadius: 6, border: 'none',
              backgroundColor: '#7B1FA2', color: '#fff', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Go to signup
          </button>
        </div>
      </TenantSignupLayout>
    );
  }

  useEffect(() => {
    const bootstrap = async () => {
      try {
        setLoadingAppointments(true);

        await Auth.signIn({ username: signupState.email, password: signupState.password });

        const tenantInput = buildTenantInput(signupState);
        try {
          await API.graphql(graphqlOperation(createTenant, { input: tenantInput }));
          console.log('[InitialAppointmentPage] Tenant created');
        } catch (err) {
          console.warn('[InitialAppointmentPage] createTenant error:', err);
        }

        // Auto-flip any pending Lead matching this email/phone to 'converted'.
        markMatchingLeadsConverted({ email: signupState.email, phone: signupState.phone });

        // Fetch appointments and drivers in parallel
        const [apptData, driversData]: [any, any] = await Promise.all([
          getAppointments(),
          API.graphql({
            query: driversByFacility,
            variables: { facilityID: signupState.facilityID },
          }),
        ]);

        const parsed = Array.isArray(apptData)
          ? apptData
          : JSON.parse((apptData as any).body ?? '[]');
        const facilityAppointments = parsed.filter(
          (appt: Appointment) => appt.facilityID === signupState.facilityID
        );
        setAppointments(facilityAppointments);

        const fetchedDrivers: Driver[] = (driversData as any)?.data?.driversByFacility?.items ?? [];
        setDrivers(fetchedDrivers);
      } catch (err) {
        console.error('[InitialAppointmentPage] bootstrap error:', err);
        setError('There was a problem preparing your appointment. Please try again.');
      } finally {
        setLoadingAppointments(false);
      }
    };

    bootstrap();
  }, [signupState]);

  const recalcSlotsForDate = (dateStr: string) => {
    setSelectedSlot('');
    if (!dateStr) { setAvailableSlots([]); return; }

    const available = getAvailableSlots(drivers, appointments, dateStr);
    const slots: SlotInfo[] = (Object.entries(available) as [SlotKey, { isPaid: boolean }][])
      .map(([slotKey, info]) => ({
        slotKey,
        time: SLOT_TIMES[slotKey],
        paid: info.isPaid,
      }))
      .sort((a, b) => a.time.localeCompare(b.time));

    setAvailableSlots(slots);
  };

  const handleDateChange = (value: string) => {
    setSelectedDate(value);
    recalcSlotsForDate(value);
    setError(null);
  };

  const handleNext = () => {
    if (!selectedDate) { setError('Please select a date for your pickup.'); return; }
    if (!selectedSlot) { setError('Please select a time window for your pickup.'); return; }

    const slot = availableSlots.find(s => s.time === selectedSlot);
    const slotPaid = slot?.paid ?? false;
    const slotKey = slot?.slotKey ?? ('morning' as SlotKey);
    const assigned = assignDriver(drivers, appointments, selectedDate, slotKey);

    const nextState = {
      ...signupState,
      dateSelected: selectedDate,
      timeSelected: selectedSlot,
      slotPaid,
      driverAssigned: assigned?.name ?? '',
      paidPricingLabel: slotPaid ? paidPricingLabel : undefined,
      address: {
        addressLine1: signupState.addressLine1,
        addressLine2: signupState.addressLine2,
        city: signupState.city,
        state: signupState.state,
        zip: signupState.zip,
        specialInstructions: signupState.specialInstructions,
        parking: signupState.parking,
        building: signupState.building,
      },
    };

    console.log('[InitialAppointmentPage] Navigating to initial pickup with state:', nextState);
    history.push('/signup/initial-pickup', nextState);
  };

  return (
    <TenantSignupLayout
      title="Schedule your first pickup"
      subtitle="Choose a date and a 2-hour window for us to come get your items."
      step={5}
      totalSteps={7}
    >
      <div style={{ ...baseFont }}>
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Pickup date</label>
          <input
            type="date"
            min={minDate}
            value={selectedDate}
            onChange={e => handleDateChange(e.target.value)}
            style={inputStyle}
          />
          <div style={hintTextStyle}>
            Slots shown are based on driver availability. Free slots are included in your plan — paid slots carry an additional moving fee.
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Arrival window</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
            {loadingAppointments && (
              <div style={hintTextStyle}>Checking availability…</div>
            )}
            {!loadingAppointments && !selectedDate && (
              <div style={hintTextStyle}>Pick a date first to see available times.</div>
            )}
            {!loadingAppointments && selectedDate && availableSlots.length === 0 && (
              <div style={hintTextStyle}>No availability on this date. Please choose another day.</div>
            )}
            {!loadingAppointments && selectedDate && availableSlots.map(slot => (
              <button
                key={slot.time}
                type="button"
                onClick={() => { setSelectedSlot(slot.time); setError(null); }}
                style={{
                  ...chipStyle,
                  ...(selectedSlot === slot.time ? chipSelectedStyle : {}),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <span>{slot.time}</span>
                <span style={{ fontSize: 11, opacity: 0.8 }}>
                  {slot.paid ? paidPricingLabel : 'Free'}
                </span>
              </button>
            ))}
          </div>
        </div>

        {error && <div style={errorTextStyle}>{error}</div>}

        <button
          type="button"
          onClick={handleNext}
          style={{
            ...baseFont,
            marginTop: 24,
            width: '100%',
            padding: '10px 16px',
            borderRadius: 6,
            border: 'none',
            backgroundColor: '#7B1FA2',
            color: '#fff',
            fontWeight: 600,
            cursor: 'pointer',
            textAlign: 'center' as const,
          }}
        >
          Next
        </button>
      </div>
    </TenantSignupLayout>
  );
};

export default InitialAppointmentPage;
